import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@shared/components/Button';
import { InputText } from '@shared/components/InputText';
import Layout from '@shared/components/Layout';
import { validateEmail } from '@shared/utils/validation';
import { translateValidationError } from '@shared/utils/validationMessages';

interface CollectEmailScreenProps {
  initialEmail?: string;
  errorText?: string;
  onEmailChange: (data: Record<string, unknown>) => void;
  onEmailSubmit: (email: string) => Promise<void>;
  onClickCancel: () => Promise<void>;
  resetError: () => void;
}

const CollectEmailScreen: React.FC<CollectEmailScreenProps> = ({
  initialEmail = '',
  errorText,
  onEmailChange,
  onEmailSubmit,
  onClickCancel,
  resetError,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setEmail(initialEmail);
  }, [initialEmail]);

  const handleEmailChange = (value: string) => {
    const nextEmail = value.trim();

    resetError();
    setEmailError(undefined);
    setEmail(nextEmail);
    onEmailChange({ email: nextEmail });
  };

  const submitEmail = async () => {
    const validation = validateEmail(email);

    if (!validation.isValid) {
      setEmailError(validation.error);
      return;
    }

    setIsSubmitting(true);

    try {
      await onEmailSubmit(email);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout
      heading={t('Add your email')}
      subHeading={t("We'll send a verification code to this email.")}
      hideLogo
    >
      <div className="space-y-6">
        <InputText
          label={t('Email')}
          type="email"
          value={email}
          maxLength={100}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            handleEmailChange(event.target.value);
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter' && !isSubmitting) {
              event.preventDefault();
              void submitEmail();
            }
          }}
          errorMessage={translateValidationError(t, emailError) || errorText}
          state={emailError || errorText ? 'error' : 'default'}
        />

        <Button
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            void submitEmail();
          }}
          variant="primary"
          disabled={email.length === 0}
          label={t('Continue')}
          state={isSubmitting ? 'loading' : 'default'}
        />

        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              void onClickCancel();
            }}
            disabled={isSubmitting}
            className="text-sm font-medium text-black hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
          >
            {t('Cancel')}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default CollectEmailScreen;
